import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import FooterNavigation from '@/components/modules/navigation/FooterNavigation';
import FooterNavigationContainer from '@/components/modules/navigation/FooterNavigationContainer';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import { MEGA_MENU_NAVIGATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import { classnames } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useMobileView from '@/hooks/useMobileView';
import { createPortal } from 'react-dom';
import { Fragment } from 'react/jsx-runtime';
import { getFooterDisplayOption } from '../helpers';
import { PageViewLayout as PageViewLayoutProps } from '../types';

const PageViewLayout = ({
  children,
  footerDisplayOption,
  wrapperClass,
  headerNavigationContainerClassName,
  type,
  hideBottomNavBar = false,
  ...navbarProps
}: PageViewLayoutProps) => {
  const { isMobileView } = useMobileView();
  const flags = useAppSelector((state) => state.flags);
  const getVariant = useGetAmplitudeExperimentVariant();

  const megaMenuContext = useMegaMenuContext();

  const isMegaMenuFeature =
    (flags[MEGA_MENU_NAVIGATION_FEATURE_FLAG]?.value === 'on' ||
      getVariant(MEGA_MENU_NAVIGATION_FEATURE_FLAG)?.value === 'on') &&
    !navbarProps.temporaryForceOldHeader;

  const showBottomNavBar = type === 'mainView' && !hideBottomNavBar && isMobileView;

  return (
    <div className={wrapperClass}>
      <NavBar
        navClass={classnames(headerNavigationContainerClassName)}
        {...navbarProps}
        type={type}
        bottomSlot={(() => {
          if (navbarProps.bottomSlot !== undefined) {
            return navbarProps.bottomSlot;
          }
          if (isMegaMenuFeature) {
            const megaMenu = (
              <MegaMenu
                {...megaMenuContext}
                renderNavigationContainer={(props) => {
                  const ref = props.navigationContainerRef.current;
                  return ref ? (
                    createPortal(<MegaMenu.Categories {...props} />, ref)
                  ) : (
                    /**
                     * need to render the navigation before the ref is mounted to make sure navigation appears on initial render
                     */
                    <div id={props.navigationContainerId} className="mega-menu-navigation-container" role="navigation">
                      <MegaMenu.Categories {...props} />
                    </div>
                  );
                }}
              />
            );

            if (megaMenuContext.activeTab) {
              return megaMenu;
            }
            /** if ref is not mounted use 56px for now for for Mega menu tabs (update if component changes) */
            const height = megaMenuContext.navRef.current?.getBoundingClientRect()?.height ?? 56;
            return <HideOnScrollWrapper height={height}>{megaMenu}</HideOnScrollWrapper>;
          }
          return undefined;
        })()}
        floatingSlot={
          navbarProps.floatingSlot ?? isMegaMenuFeature ? (
            <Fragment>{megaMenuContext.renderNavigationContainer(megaMenuContext)}</Fragment>
          ) : null
        }
      />
      {children}
      {type === 'mainView' && (
        <div
          className={`${
            footerDisplayOption?.from && footerDisplayOption.to ? getFooterDisplayOption(footerDisplayOption) : ''
          }`}>
          <FooterNavigationContainer>
            <FooterNavigation />
          </FooterNavigationContainer>
        </div>
      )}
      {showBottomNavBar && !megaMenuContext.activeTab && <BottomNavBar source={navbarProps.source} />}
    </div>
  );
};

export default PageViewLayout;
